import React, { useContext, useState } from "react"
import {
	Flex,
	Text,
	Button,
	Accordion,
	INNER_MAX_WIDTH,
} from "@capioweb/ui-components"
import { LocalesContext } from "../../../context"
import AnimateHeight from "react-animate-height"
import { CareerFilterContext } from "../context/CareerFilterContext"
import { FilterSection } from "./FilterSection"
import { sendOpenFilterDataLayerPush } from "../../../utils"

interface CareerFilterProps {
	countyFilter: CareerFilterValue[]
	jobTypeFilter: CareerFilterValue[]
	employmentTypeFilter: CareerFilterValue[]
}

export const CareerFilter: React.FC<CareerFilterProps> = ({
	countyFilter,
	jobTypeFilter,
	employmentTypeFilter,
}) => {
	const { translate, translationKeys } = React.useContext(LocalesContext)

	// Toggle for filter
	const { activeCountyIds, activeJobTypeIds, activeEmploymentTypeIds } =
		useContext(CareerFilterContext)
	const [filterOpen, setFilterOpen] = useState(
		activeJobTypeIds.length > 0 ||
			activeCountyIds.length > 0 ||
			activeEmploymentTypeIds.length > 0
	)

	return (
		<>
			<Flex
				w={1}
				py={3}
				justifyContent="center"
				backgroundColor="blue11"
				flexDirection="column"
				alignItems="center"
			>
				<Flex>
					<Button
						variant="ghost-navy"
						icon="filter"
						textColor="navy"
						onClick={() => {
							setFilterOpen(!filterOpen)
							sendOpenFilterDataLayerPush(filterOpen ? "close" : "open")
						}}
					>
						{`${
							filterOpen
								? translate(translationKeys.close)
								: translate(translationKeys.open)
						} ${translate(translationKeys.filter)}`}
					</Button>
				</Flex>
			</Flex>
			<AnimateHeight height={filterOpen ? "auto" : 0}>
				<Flex w={1} px={3} backgroundColor="blue11">
					<Flex w={1} flexDirection="column" maxw={INNER_MAX_WIDTH} m="0 auto">
						{/* Filter layout for screens >= l */}
						<Flex w={1} display={{ _: "none", l: "block" }}>
							{/* County filters */}
							{countyFilter.length > 0 && (
								<FilterSection
									filterType="county"
									heading={translate(translationKeys.regions)}
									filterValues={countyFilter}
								/>
							)}

							{/* Job type filters */}
							{jobTypeFilter.length > 0 && (
								<FilterSection
									filterType="jobType"
									heading={translate(translationKeys.jobType)}
									filterValues={jobTypeFilter}
								/>
							)}
							{/* Employment type filters */}
							{employmentTypeFilter.length > 0 && (
								<FilterSection
									filterType="employmentType"
									heading={translate(translationKeys.employmentType)}
									filterValues={employmentTypeFilter}
									lastChild
								/>
							)}
						</Flex>

						{/* Filter layout for screens < l */}
						<Flex
							w={1}
							display={{ _: "flex", l: "none" }}
							flexDirection="column"
							pb={4.5}
						>
							<Accordion>
								{/* County filters */}
								<Accordion.Header id={translate(translationKeys.county)}>
									<Text fontSize={2.5} lineHeight={3} fontWeight="bold" mb={0}>
										{translate(translationKeys.county)}
									</Text>
								</Accordion.Header>
								<Accordion.Panel id={translate(translationKeys.county)}>
									<FilterSection
										filterType="county"
										filterValues={countyFilter}
									/>
								</Accordion.Panel>

								{/* Job type filter */}
								{jobTypeFilter.length > 0 && (
									<>
										<Accordion.Header id={translate(translationKeys.jobType)}>
											<Text
												fontSize={2.5}
												lineHeight={3}
												fontWeight="bold"
												mb={0}
											>
												{translate(translationKeys.jobType)}
											</Text>
										</Accordion.Header>
										<Accordion.Panel id={translate(translationKeys.jobType)}>
											<Flex flexDirection="column" px={2} pt={1}>
												<FilterSection
													filterType="jobType"
													filterValues={jobTypeFilter}
												/>
											</Flex>
										</Accordion.Panel>
									</>
								)}
								{/* Employment type filter */}
								{employmentTypeFilter.length > 0 && (
									<>
										<Accordion.Header
											id={translate(translationKeys.employmentType)}
										>
											<Text
												fontSize={2.5}
												lineHeight={3}
												fontWeight="bold"
												mb={0}
											>
												{translate(translationKeys.employmentType)}
											</Text>
										</Accordion.Header>
										<Accordion.Panel
											id={translate(translationKeys.employmentType)}
										>
											<Flex flexDirection="column" px={2} pt={1}>
												<FilterSection
													filterType="employmentType"
													filterValues={employmentTypeFilter}
													lastChild
												/>
											</Flex>
										</Accordion.Panel>
									</>
								)}
							</Accordion>
						</Flex>
					</Flex>
				</Flex>
			</AnimateHeight>
		</>
	)
}
